import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import {
  Alert,
  Button,
  Input,
  Modal,
  Spin,
  Switch,
  message,
} from 'antd';
import { Copy, ShieldCheck, WarningCircle } from '@phosphor-icons/react';
import {
  getSamlConnection,
  createSamlConnection,
  updateSamlConnection,
  deleteSamlConnection,
} from '../../../actions/singleSignOn';
import { captureAnalytics } from '../../../actions/preload';
import { startSsoSignIn } from '../../../util/ssoSignIn';
import './single-sign-on-settings.scss';

// Where "Test sign-in" comes back to: this tab (UserProfile's tabKeys).
export const SETTINGS_RETURN_PATH = '/app/user?tab=single_sign_on';
const EXPIRY_WARNING_DAYS = 60;

const COPY = {
  intro: 'Let your team sign in to Medsender with their Microsoft work account. '
    + 'You control who has access in Microsoft Entra ID.',
  loadFailed: 'Unable to load single sign-on settings.',
  saveFailed: 'Unable to save single sign-on settings. Please try again.',
  copied: 'Copied',
  copyFailed: 'Unable to copy. Select the text and copy it instead.',
};

const errorText = (response, fallback) => response?.response?.data?.error || fallback;

const formatDate = (value) => (value ? moment(value).format('MMM D, YYYY') : null);

// 'expired', 'soon' (within EXPIRY_WARNING_DAYS) or null.
const certificateWarning = (notAfter) => {
  if (!notAfter) return null;
  const daysLeft = moment(notAfter).diff(moment(), 'days', true);
  if (daysLeft < 0) return 'expired';
  return daysLeft < EXPIRY_WARNING_DAYS ? 'soon' : null;
};

const pathOf = (url) => {
  try {
    const parsed = new URL(url, window.location.origin);
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
};

const CopyField = ({
  label, hint, value, field, onCopy,
}) => (
  <div className="sso-settings__copy-field">
    <div className="sso-settings__label">{label}</div>
    {hint && <div className="sso-settings__hint">{hint}</div>}
    <div className="sso-settings__copy-row">
      <Input readOnly value={value} aria-label={label} className="sso-settings__copy-input" />
      <Button
        className="sso-settings__copy"
        icon={<Copy size={16} />}
        aria-label={`Copy ${label}`}
        onClick={() => onCopy(field, value)}
      >
        Copy
      </Button>
    </div>
  </div>
);

CopyField.propTypes = {
  label: PropTypes.string.isRequired,
  hint: PropTypes.string,
  value: PropTypes.string.isRequired,
  field: PropTypes.string.isRequired,
  onCopy: PropTypes.func.isRequired,
};

CopyField.defaultProps = {
  hint: null,
};

const Step = ({ number, title, children }) => (
  <section className="sso-settings__step">
    <h3 className="sso-settings__step-title">
      <span className="sso-settings__step-number">{number}</span>
      {title}
    </h3>
    <div className="sso-settings__step-body">{children}</div>
  </section>
);

Step.propTypes = {
  number: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const SingleSignOnSettings = ({
  getSamlConnection: loadConnection,
  createSamlConnection: createConnection,
  updateSamlConnection: updateConnection,
  deleteSamlConnection: deleteConnection,
  captureAnalyticsEvent,
}) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [settings, setSettings] = useState(null);
  // Which change is in flight ('create', 'metadata', 'enabled', 'force_authn', 'enforced', 'remove').
  const [busy, setBusy] = useState(null);
  // An error shown under the step it belongs to: { step, text }.
  const [stepError, setStepError] = useState(null);
  const [metadataUrl, setMetadataUrl] = useState('');
  const [confirmEnforce, setConfirmEnforce] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const mounted = useRef(true);
  const fileInput = useRef(null);
  // One change at a time: every response is the whole settings payload, so two
  // in flight could land out of order and show values the server no longer has.
  const saving = busy !== null;

  useEffect(() => {
    mounted.current = true;
    const load = async () => {
      const response = await loadConnection();
      if (!mounted.current) return;
      if (response?.error) {
        setLoadError(errorText(response, COPY.loadFailed));
      } else {
        setSettings(response?.data || null);
      }
      setLoading(false);
    };
    load();
    return () => { mounted.current = false; };
  }, []);

  // Runs one change; the response is the whole settings payload again.
  const apply = async (step, request) => {
    setBusy(step);
    setStepError(null);
    const response = await request();
    if (!mounted.current) return false;
    setBusy(null);
    if (response?.error) {
      setStepError({ step, text: errorText(response, COPY.saveFailed) });
      return false;
    }
    setSettings(response?.data || null);
    return true;
  };

  const startSetup = async () => {
    captureAnalyticsEvent('saml_sso_setup_started');
    await apply('create', createConnection);
  };

  const saveMetadataUrl = async () => {
    const url = metadataUrl.trim();
    if (!url) {
      setStepError({ step: 'metadata', text: 'Paste the App Federation Metadata Url from Entra.' });
      return;
    }
    if (await apply('metadata', () => updateConnection({ metadata_url: url }))) {
      captureAnalyticsEvent('saml_sso_metadata_saved', { source: 'url' });
      setMetadataUrl('');
    }
  };

  const uploadMetadataFile = async (event) => {
    const file = event.target.files?.[0];
    // Lets the same file be picked again after a fix in Entra.
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
    if (!file) return;

    let xml;
    try {
      xml = await file.text();
    } catch {
      setStepError({ step: 'metadata', text: 'Unable to read that file. Download it from Entra again.' });
      return;
    }
    if (await apply('metadata', () => updateConnection({ metadata_xml: xml }))) {
      captureAnalyticsEvent('saml_sso_metadata_saved', { source: 'xml' });
    }
  };

  const toggleEnabled = async (enabled) => {
    if (await apply('enabled', () => updateConnection({ enabled }))) {
      captureAnalyticsEvent('saml_sso_access_toggled', { enabled });
    }
  };

  const toggleForceAuthn = async (forceAuthn) => {
    if (await apply('force_authn', () => updateConnection({ force_authn: forceAuthn }))) {
      captureAnalyticsEvent('saml_sso_force_authn_toggled', { enabled: forceAuthn });
    }
  };

  const toggleEnforced = async (enforced) => {
    if (enforced) {
      setStepError(null);
      setConfirmEnforce(true);
      return;
    }
    if (await apply('enforced', () => updateConnection({ enforced: false }))) {
      captureAnalyticsEvent('saml_sso_enforcement_toggled', { enforced: false });
    }
  };

  const confirmEnforcement = async () => {
    setConfirmEnforce(false);
    if (await apply('enforced', () => updateConnection({ enforced: true }))) {
      captureAnalyticsEvent('saml_sso_enforcement_toggled', { enforced: true });
    }
  };

  const removeConnection = async () => {
    setConfirmRemove(false);
    if (await apply('remove', deleteConnection)) {
      captureAnalyticsEvent('saml_sso_removed');
      message.success('Single sign-on removed');
    }
  };

  const testSignIn = () => {
    captureAnalyticsEvent('saml_sso_test_started');
    startSsoSignIn(pathOf(settings.service_provider.login_url), { returnPath: SETTINGS_RETURN_PATH });
  };

  const copyValue = async (field, value) => {
    try {
      await navigator.clipboard.writeText(value);
      captureAnalyticsEvent('saml_sso_value_copied', { field });
      message.success(COPY.copied);
    } catch {
      message.error(COPY.copyFailed);
    }
  };

  const renderStepError = (step) => (
    stepError?.step === step
      ? <div className="sso-settings__error" role="alert">{stepError.text}</div>
      : null
  );

  if (loading) {
    return <div className="sso-settings sso-settings--loading"><Spin size="large" /></div>;
  }

  if (loadError) {
    return (
      <div className="sso-settings">
        <Alert type="error" showIcon message={loadError} />
      </div>
    );
  }

  const connection = settings?.saml_connection;
  const serviceProvider = settings?.service_provider;
  const allowedDomains = settings?.allowed_domains || [];
  const outsideCount = settings?.users_outside_allowed_domains || 0;

  const domainsNote = (
    <div className="sso-settings__domains">
      {allowedDomains.length > 0
        ? `People can sign in with single sign-on when their email ends in: ${allowedDomains.map((domain) => `@${domain}`).join(', ')}.`
        : 'No email domains are set up for single sign-on yet.'}
      {' '}
      Contact Medsender support to add a domain.
    </div>
  );

  if (!connection) {
    return (
      <div className="sso-settings">
        <h2 className="sso-settings__title">Single sign-on</h2>
        <p className="sso-settings__intro">{COPY.intro}</p>
        {domainsNote}
        <div className="sso-settings__empty-actions">
          <Button type="primary" loading={busy === 'create'} onClick={startSetup}>
            Set up single sign-on
          </Button>
          {renderStepError('create')}
        </div>
      </div>
    );
  }

  const canEnforce = connection.enabled && Boolean(connection.verified_at);
  let status = { text: 'Off', on: false };
  if (connection.enforced) status = { text: 'Required for everyone', on: true };
  else if (connection.enabled) status = { text: 'On (passwords still work)', on: true };

  return (
    <div className="sso-settings">
      <div className="sso-settings__header">
        <h2 className="sso-settings__title">Single sign-on</h2>
        <span className={`sso-settings__badge${status.on ? ' sso-settings__badge--on' : ''}`}>
          {status.on && <ShieldCheck size={16} weight="fill" />}
          {status.text}
        </span>
      </div>
      <p className="sso-settings__intro">{COPY.intro}</p>

      <Step number={1} title="Create the app in Microsoft Entra">
        <p className="sso-settings__text">
          In the Microsoft Entra admin center, go to Enterprise applications &gt; New application &gt;
          Create your own application, and choose &quot;Integrate any other application you don&apos;t
          find in the gallery (Non-gallery)&quot;. Open Single sign-on, choose SAML, and paste these values
          into Basic SAML Configuration:
        </p>
        <CopyField
          label="Identifier (Entity ID)"
          field="entity_id"
          value={serviceProvider.entity_id}
          onCopy={copyValue}
        />
        <CopyField
          label="Reply URL (Assertion Consumer Service URL)"
          field="acs_url"
          value={serviceProvider.acs_url}
          onCopy={copyValue}
        />
        <CopyField
          label="Sign on URL"
          hint="Lets people start from the Microsoft My Apps portal."
          field="login_url"
          value={serviceProvider.login_url}
          onCopy={copyValue}
        />
        <p className="sso-settings__text">
          Then, under Users and groups, assign the people or groups who should use Medsender.
        </p>
      </Step>

      <Step number={2} title="Connect Entra to Medsender">
        <p className="sso-settings__text">
          In Entra&apos;s SAML Certificates section, copy the App Federation Metadata Url and paste it
          here, or download the Federation Metadata XML file and upload it.
        </p>
        <div className="sso-settings__label">App Federation Metadata Url</div>
        <div className="sso-settings__copy-row">
          <Input
            aria-label="App Federation Metadata Url"
            placeholder="https://login.microsoftonline.com/.../federationmetadata.xml?appid=..."
            value={metadataUrl}
            disabled={saving}
            onChange={(event) => setMetadataUrl(event.target.value)}
            onPressEnter={saveMetadataUrl}
          />
          <Button type="primary" loading={busy === 'metadata'} disabled={saving} onClick={saveMetadataUrl}>
            Save
          </Button>
        </div>
        <div className="sso-settings__upload">
          <span className="sso-settings__hint">or</span>
          <Button disabled={saving} onClick={() => fileInput.current?.click()}>
            Upload Federation Metadata XML
          </Button>
          <input
            ref={fileInput}
            type="file"
            accept=".xml,application/xml,text/xml"
            className="sso-settings__file"
            data-testid="sso-metadata-file"
            onChange={uploadMetadataFile}
          />
        </div>
        {renderStepError('metadata')}
        {connection.configured && (
          <div className="sso-settings__idp">
            <div className="sso-settings__idp-row">
              <span className="sso-settings__label">Microsoft Entra Identifier</span>
              <span className="sso-settings__value">{connection.idp_entity_id}</span>
            </div>
            <div className="sso-settings__idp-row">
              <span className="sso-settings__label">Login URL</span>
              <span className="sso-settings__value">{connection.idp_sso_url}</span>
            </div>
            {(connection.certificates || []).map((certificate) => {
              const warning = certificateWarning(certificate.not_after);
              return (
                <div className="sso-settings__idp-row" key={certificate.sha256_fingerprint}>
                  <span className="sso-settings__label">Signing certificate</span>
                  <span className={`sso-settings__value${warning ? ` sso-settings__value--${warning}` : ''}`}>
                    {warning && <WarningCircle size={16} weight="fill" />}
                    {`${warning === 'expired' ? 'Expired' : 'Expires'} ${formatDate(certificate.not_after)}`}
                  </span>
                  <span className="sso-settings__fingerprint">{certificate.sha256_fingerprint}</span>
                  {warning && (
                    <span className="sso-settings__hint">
                      Create a new SAML signing certificate in Entra, make it active, then save the metadata here again.
                    </span>
                  )}
                </div>
              );
            })}
            {connection.metadata_refreshed_at && (
              <div className="sso-settings__hint">{`Updated ${formatDate(connection.metadata_refreshed_at)}`}</div>
            )}
          </div>
        )}
      </Step>

      <Step number={3} title="Turn on and test">
        <div className="sso-settings__switch-row">
          <Switch
            aria-label="Allow single sign-on"
            checked={connection.enabled}
            disabled={!connection.configured || saving || connection.enforced}
            loading={busy === 'enabled'}
            onChange={toggleEnabled}
          />
          <div>
            <div className="sso-settings__label">Allow single sign-on</div>
            <div className="sso-settings__hint">
              {connection.configured
                ? 'People can choose Log in with SSO. Passwords keep working.'
                : 'Connect Entra first (step 2).'}
            </div>
          </div>
        </div>
        {renderStepError('enabled')}
        <div className="sso-settings__test-row">
          <Button disabled={!connection.enabled || saving} onClick={testSignIn}>Test sign-in</Button>
          <span className="sso-settings__hint">
            {connection.verified_at ? `Tested ${formatDate(connection.verified_at)}` : 'Not tested yet'}
          </span>
        </div>
        <div className="sso-settings__hint">
          Signs you in to Medsender through Entra and brings you back here.
        </div>
      </Step>

      <Step number={4} title="Shared computers (optional)">
        <div className="sso-settings__switch-row">
          <Switch
            aria-label="Ask for Microsoft sign-in every time"
            checked={Boolean(connection.force_authn)}
            disabled={!connection.enabled || saving}
            loading={busy === 'force_authn'}
            onChange={toggleForceAuthn}
          />
          <div>
            <div className="sso-settings__label">Ask for Microsoft sign-in every time</div>
            <div className="sso-settings__hint">
              {connection.enabled
                ? 'Recommended for shared computers, like a front desk. People sign in to Microsoft each time '
                  + 'they open Medsender, even if they\'re already signed in to Microsoft in that browser. '
                  + 'Log in with Microsoft goes through single sign-on too.'
                : 'Turn on single sign-on first (step 3).'}
            </div>
          </div>
        </div>
        {renderStepError('force_authn')}
      </Step>

      <Step number={5} title="Require single sign-on">
        <div className="sso-settings__switch-row">
          <Switch
            aria-label="Require single sign-on for everyone"
            checked={connection.enforced}
            disabled={(!canEnforce && !connection.enforced) || saving}
            loading={busy === 'enforced'}
            onChange={toggleEnforced}
          />
          <div>
            <div className="sso-settings__label">Require single sign-on for everyone</div>
            <div className="sso-settings__hint">
              {canEnforce || connection.enforced
                ? 'Passwords, password reset and Log in with Microsoft stop working for this organization.'
                : 'Turn on single sign-on and test it first.'}
            </div>
          </div>
        </div>
        {renderStepError('enforced')}
        {domainsNote}
        {outsideCount > 0 && (
          <div className="sso-settings__warning" role="note">
            <WarningCircle size={16} weight="fill" />
            {`${outsideCount} ${outsideCount === 1 ? 'person has an email address' : 'people have email addresses'} `
              + 'outside these domains and won\'t be able to sign in once single sign-on is required.'}
          </div>
        )}
      </Step>

      <div className="sso-settings__remove">
        <Button danger loading={busy === 'remove'} disabled={saving} onClick={() => setConfirmRemove(true)}>
          Remove single sign-on
        </Button>
        {renderStepError('remove')}
      </div>

      <Modal
        visible={confirmEnforce}
        title="Require single sign-on?"
        className="sso-settings-modal"
        onCancel={() => setConfirmEnforce(false)}
        footer={[
          <Button key="cancel" onClick={() => setConfirmEnforce(false)}>Cancel</Button>,
          <Button key="confirm" type="primary" disabled={saving} onClick={confirmEnforcement}>Require single sign-on</Button>,
        ]}
      >
        <div className="sso-settings__confirm">
          <p>Everyone in your organization will have to sign in through Microsoft.</p>
          <p>
            Passwords, password reset and Log in with Microsoft will stop working for this
            organization, and anyone signed in another way will be signed out.
          </p>
          {outsideCount > 0 && (
            <p className="sso-settings__confirm-warning">
              {`${outsideCount} ${outsideCount === 1 ? 'person' : 'people'} with email addresses outside `
                + `${allowedDomains.map((domain) => `@${domain}`).join(', ') || 'your domains'} `
                + 'won\'t be able to sign in.'}
            </p>
          )}
          <p>If your Microsoft sign-in stops working, Medsender support can turn this off.</p>
        </div>
      </Modal>

      <Modal
        visible={confirmRemove}
        title="Remove single sign-on?"
        className="sso-settings-modal"
        onCancel={() => setConfirmRemove(false)}
        footer={[
          <Button key="cancel" onClick={() => setConfirmRemove(false)}>Cancel</Button>,
          <Button key="confirm" type="primary" danger disabled={saving} onClick={removeConnection}>Remove</Button>,
        ]}
      >
        <p>
          People will sign in with their passwords again. To use single sign-on later, you&apos;ll
          set up the Entra app again with new values.
        </p>
      </Modal>
    </div>
  );
};

SingleSignOnSettings.propTypes = {
  getSamlConnection: PropTypes.func.isRequired,
  createSamlConnection: PropTypes.func.isRequired,
  updateSamlConnection: PropTypes.func.isRequired,
  deleteSamlConnection: PropTypes.func.isRequired,
  captureAnalyticsEvent: PropTypes.func,
};

SingleSignOnSettings.defaultProps = {
  captureAnalyticsEvent: captureAnalytics,
};

const mapDispatchToProps = {
  getSamlConnection,
  createSamlConnection,
  updateSamlConnection,
  deleteSamlConnection,
};

export { SingleSignOnSettings };
export default connect(null, mapDispatchToProps)(SingleSignOnSettings);
